// Triggering a reload to fix a potential linter issue.
import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { storage } from "./utils/storage";
import { getSafeRedirectPath } from "./utils/redirect";
import {
  clearCachedAuthIdentity,
  clearAuthSession,
  getWebAuthSessionChange,
  getAuthToken,
  hasResolvedAuthSession,
  hasWebAuthSession,
  markWebAuthSession,
  shouldRestoreWebAuthSession,
} from "./lib/auth";
import { api, buildLoginRedirectPath } from "./lib/axios";
import AuthenticatedLayout from "./components/layouts/AuthenticatedLayout";
import { PermissionSyncObserver } from "./components/auth/PermissionSyncObserver";
import { RouteLoadingFallback } from "./components/layouts/RouteLoadingFallback";
import { ErrorBoundary as AppErrorBoundary } from "./components/ErrorBoundary";
import { LegacyPrintRedirect } from "./routes/printStudioRoute";
import { WorkspaceAccessGuard } from "./features/workspace-access";

// QA Toolbar — only loaded when VITE_QA_MODE is set (never in production builds)
const QAToolbar =
  import.meta.env.VITE_QA_MODE === "true"
    ? lazy(() => import("./components/QAToolbar"))
    : null;

// Performance diagnostics overlay — QA-gated, never in production builds
const PerfDiagnosticsOverlay =
  import.meta.env.VITE_QA_MODE === "true"
    ? lazy(() => import("./components/PerformanceDiagnosticsOverlay"))
    : null;

// Auto-reload on stale chunk errors (after deploy, old chunk hashes no longer exist)
function lazyWithRetry(
  importFn: () => Promise<{ default: React.ComponentType }>
) {
  return lazy(() =>
    importFn().catch((error) => {
      // Chunk load failure — likely a stale deploy. Reload once.
      if (
        error.message?.includes(
          "Failed to fetch dynamically imported module"
        ) ||
        error.message?.includes("Loading chunk") ||
        error.message?.includes("MIME type")
      ) {
        const hasReloaded = sessionStorage.getItem("chunk_reload");
        if (!hasReloaded) {
          sessionStorage.setItem("chunk_reload", "1");
          window.location.reload();
          return { default: () => null } as { default: React.ComponentType };
        }
        sessionStorage.removeItem("chunk_reload");
      }
      throw error;
    })
  );
}

// Lazy loaded (heavy routes) — with auto-reload on stale chunks
const Home = lazyWithRetry(() => import("./routes/home.tsx"));
const NotFoundPage = lazyWithRetry(() => import("./routes/not-found.tsx"));
const Login = lazyWithRetry(() => import("./routes/login.tsx"));
const SsoCallback = lazyWithRetry(() => import("./routes/sso.callback.tsx"));
const PrivacyPolicyPage = lazyWithRetry(
  () => import("./routes/privacy-policy.tsx")
);
const TermsAndConditionsPage = lazyWithRetry(
  () => import("./routes/terms-and-conditions.tsx")
);
const GISPage = lazyWithRetry(() => import("./routes/gis.tsx"));
const GISProjectPage = lazyWithRetry(
  () => import("./routes/gis.$projectId.tsx")
);
const DashboardStudioPage = lazyWithRetry(
  () => import("./routes/studio.$projectId.$shareId.tsx")
);
const MapPrintStudioPage = lazyWithRetry(
  () => import("./routes/studio.$projectId.print.tsx")
);
const TimeclockLayout = lazyWithRetry(() => import("./routes/timeclock.tsx"));
const TimeclockDashboard = lazyWithRetry(
  () => import("./routes/timeclock._index.tsx")
);
const TimeclockTimesheets = lazyWithRetry(
  () => import("./routes/timeclock.timesheets.tsx")
);
const TimeclockWorksites = lazyWithRetry(
  () => import("./routes/timeclock.worksites.tsx")
);
const TimeclockShifts = lazyWithRetry(
  () => import("./routes/timeclock.shifts.tsx")
);
const TimeclockExceptions = lazyWithRetry(
  () => import("./routes/timeclock.exceptions.tsx")
);
const TenantOperations = lazyWithRetry(
  () => import("./routes/tenant-operations.tsx")
);
const Profile = lazyWithRetry(() => import("./routes/profile.tsx"));
const Projects = lazyWithRetry(() => import("./routes/projects.tsx"));
const ProjectPage = lazyWithRetry(
  () => import("./routes/projects.$projectId.tsx")
);
const ProjectWorkspaceAccessPage = lazyWithRetry(
  () => import("./routes/projects.$projectId.workspace-access.tsx")
);
const ProjectAssistantPage = lazyWithRetry(() => import("./routes/project-assistant.tsx"));
const ProjectOperationsPage = lazyWithRetry(
  () => import("./routes/projects.$projectId.operations.tsx")
);
const SolutionWorkspaceRoute = lazyWithRetry(
  () => import("./routes/projects.$projectId.solutions.$templateSlug.tsx")
);
const RecordWorkspaceRoute = lazyWithRetry(
  () => import("./routes/projects.$projectId.records.$viewKey.tsx")
);
const WorkflowEditorRoute = lazyWithRetry(
  () => import("./routes/projects.$projectId.operations.workflows.editor.tsx")
);
const WorkflowRunsRoute = lazyWithRetry(
  () => import("./routes/projects.$projectId.operations.workflows.runs.tsx")
);
const WorkflowHealthRoute = lazyWithRetry(
  () => import("./routes/projects.$projectId.operations.workflows.health.tsx")
);
const NotificationsPage = lazyWithRetry(
  () => import("./routes/notifications.tsx")
);
const TicketsPage = lazyWithRetry(() => import("./routes/tickets.tsx"));
const TicketDetailPage = lazyWithRetry(
  () => import("./routes/tickets.$ticketId.tsx")
);
const DataResearchProviderPage = lazyWithRetry(
  () => import("./routes/data-research-provider.tsx")
);
const SharePage = lazyWithRetry(() => import("./routes/share.$token.tsx"));
const ClientPortalPage = lazyWithRetry(
  () => import("./routes/portal.$token.tsx")
);
const InviteAcceptPage = lazyWithRetry(
  () => import("./routes/invite.$token.tsx")
);
const ExportDownloadPage = lazyWithRetry(
  () => import("./routes/exports-download.tsx")
);
const OrgAdminLayout = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.tsx")
);
const OrgAdminOverview = lazyWithRetry(
  () => import("./routes/org.$orgId.admin._index.tsx")
);
const OrgAdminMembers = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.members.tsx")
);
const OrgAdminUnits = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.units.tsx")
);
const OrgAdminInvites = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.invites.tsx")
);
const OrgAdminSettings = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.settings.tsx")
);
const OrgAdminIntegrations = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.integrations.tsx")
);
const OrgAdminAudit = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.audit.tsx")
);
const OrgAdminClients = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.clients.tsx")
);
const OrgAdminOrgChart = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.orgchart.tsx")
);
const OrgAdminAccess = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.access.tsx")
);
const OrgAdminDeployment = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.deployment.tsx")
);
const OrgAdminCustomerOperations = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.customer-operations.tsx")
);
const OrgAdminExports = lazyWithRetry(
  () => import("./routes/org.$orgId.admin.exports.tsx")
);

function RouteSuspense({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteLoadingFallback pathname={location.pathname} />}>
      {children}
    </Suspense>
  );
}

function AuthSessionFallback() {
  const location = useLocation();
  return <RouteLoadingFallback pathname={location.pathname} />;
}

function AppRoutes() {
  const location = useLocation();
  const [hasCheckedCookieSession, setHasCheckedCookieSession] = useState(() =>
    Boolean(getAuthToken())
  );
  const [, bumpAuthSnapshot] = useState(0);
  const isAuthenticated = hasResolvedAuthSession(hasCheckedCookieSession);
  const isLoginPage = location.pathname === "/login";
  const nextPath = getSafeRedirectPath(
    new URLSearchParams(location.search).get("next")
  );

  useEffect(() => {
    const handleWebAuthStorage = (event: StorageEvent) => {
      const change = getWebAuthSessionChange(event.key, event.newValue);
      if (!change) return;

      // The HttpOnly cookie is shared by every tab, while auth_token lives in
      // sessionStorage. Drop this tab's cached identity whenever another tab
      // changes the browser-wide cookie session so no stale account data can
      // render while the new cookie session is being restored.
      clearCachedAuthIdentity();

      if (change === "signed-out") {
        window.location.href = buildLoginRedirectPath(window.location);
        return;
      }

      window.location.reload();
    };

    window.addEventListener("storage", handleWebAuthStorage);
    return () => window.removeEventListener("storage", handleWebAuthStorage);
  }, []);

  useEffect(() => {
    if (getAuthToken()) {
      setHasCheckedCookieSession(true);
      return;
    }

    // A signed-out browser has no cookie-session marker. Do not turn that
    // normal state into a noisy refresh-token 401 on every login-page load.
    if (!shouldRestoreWebAuthSession()) {
      setHasCheckedCookieSession(true);
      return;
    }

    let isMounted = true;
    const hadWebAuthSession = hasWebAuthSession();
    void api
      .post(
        "/users/users/refresh_token/",
        { platform: "web" },
        {
          __forceCsrf: true,
          __skipAuthRedirect: true,
        }
      )
      .then((response) => {
        if (!isMounted) return;

        const data = response.data as {
          token?: unknown;
          auth_cookie?: unknown;
          csrf_token?: unknown;
        };
        if (typeof data.token === "string" && data.token) {
          storage.setItem("auth_token", data.token);
        }
        if (data.auth_cookie === true) {
          markWebAuthSession(data.csrf_token);
        }
        bumpAuthSnapshot((version) => version + 1);
      })
      .catch(() => {
        if (!isMounted) return;
        if (hadWebAuthSession) {
          clearAuthSession();
          storage.removeItem("user_info");
          bumpAuthSnapshot((version) => version + 1);
        }
      })
      .finally(() => {
        if (isMounted) {
          setHasCheckedCookieSession(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!hasCheckedCookieSession && !isAuthenticated) {
    return <AuthSessionFallback />;
  }

  // If not authenticated and not on login page, redirect to login
  if (!isAuthenticated && !isLoginPage) {
    const intendedPath = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(intendedPath)}`}
        replace
      />
    );
  }

  // If authenticated and on login page, redirect to home
  if (isAuthenticated && isLoginPage) {
    return <Navigate to={nextPath} replace />;
  }

  // If on login page, just render the outlet
  if (isLoginPage) {
    return <Outlet />;
  }

  // Otherwise, render the authenticated layout
  // PermissionSyncObserver lives here (not in Providers) so it re-renders
  // after the async session restore/login and opens its realtime subscription.
  return (
    <AuthenticatedLayout>
      <PermissionSyncObserver />
      <Outlet />
    </AuthenticatedLayout>
  );
}

export default function App() {
  return (
    <LazyMotion features={domAnimation}>
      <AppErrorBoundary>
        {QAToolbar && (
          <Suspense fallback={null}>
            <QAToolbar />
          </Suspense>
        )}
        {PerfDiagnosticsOverlay && (
          <Suspense fallback={null}>
            <PerfDiagnosticsOverlay />
          </Suspense>
        )}
        <Routes>
          {/* Public routes (no auth required) */}
          <Route
            path="/share/:token"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <SharePage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/portal/:token"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <ClientPortalPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/invite/:token"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <InviteAcceptPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <PrivacyPolicyPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <TermsAndConditionsPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/sso/callback"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <SsoCallback />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          <Route
            path="/exports/download/:jobId/*"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <ExportDownloadPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          {/* Studio — full-screen takeover, auth gated by the API (PATCH requires
            session). Lives outside AuthenticatedLayout so the app navbar never
            collides with the floating Studio chrome. */}
          <Route
            path="/gis/:projectId/studio"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <MapPrintStudioPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
          {/* Legacy alias — older menus / bookmarks redirect to the new path. */}
          <Route
            path="/studio/:projectId/print"
            element={<LegacyPrintRedirect />}
          />
          <Route
            path="/studio/:projectId/:shareId"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <DashboardStudioPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />

          {/* Authenticated routes */}
          <Route element={<AppRoutes />}>
            <Route
              path="/login"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <Login />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <Projects />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/workspace-access"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectWorkspaceAccessPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/intelligence/context/assistant"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectAssistantPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectOperationsPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/solutions/:templateSlug"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <SolutionWorkspaceRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/records/:viewKey"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <RecordWorkspaceRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/workflows/new"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <WorkflowEditorRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/workflows/health"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <WorkflowHealthRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/workflows/:ruleId/runs"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <WorkflowRunsRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/workflows/:ruleId"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <WorkflowEditorRoute />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/:section"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectOperationsPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/projects/:projectId/operations/:section/*"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <ProjectOperationsPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/home"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <Home />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/gis"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <GISPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/gis/:projectId"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <WorkspaceAccessGuard workspace="gis">
                      <GISProjectPage />
                    </WorkspaceAccessGuard>
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/profile"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <Profile />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/tenant-operations"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <TenantOperations />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/settings"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <Profile />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/timeclock"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <TimeclockLayout />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            >
              <Route
                index
                element={
                  <RouteSuspense>
                    <AppErrorBoundary>
                      <TimeclockDashboard />
                    </AppErrorBoundary>
                  </RouteSuspense>
                }
              />
              <Route
                path="timesheets"
                element={
                  <RouteSuspense>
                    <AppErrorBoundary>
                      <TimeclockTimesheets />
                    </AppErrorBoundary>
                  </RouteSuspense>
                }
              />
              <Route
                path="worksites"
                element={
                  <RouteSuspense>
                    <AppErrorBoundary>
                      <TimeclockWorksites />
                    </AppErrorBoundary>
                  </RouteSuspense>
                }
              />
              <Route
                path="shifts"
                element={
                  <RouteSuspense>
                    <AppErrorBoundary>
                      <TimeclockShifts />
                    </AppErrorBoundary>
                  </RouteSuspense>
                }
              />
              <Route
                path="exceptions"
                element={
                  <RouteSuspense>
                    <AppErrorBoundary>
                      <TimeclockExceptions />
                    </AppErrorBoundary>
                  </RouteSuspense>
                }
              />
            </Route>
            <Route
              path="/notifications"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <NotificationsPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/tickets"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <TicketsPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/tickets/:ticketId"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <TicketDetailPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/data-research/inegi"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <DataResearchProviderPage />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            />
            <Route
              path="/inegi-research"
              element={<Navigate to="/data-research/inegi" replace />}
            />
            <Route
              path="/org/:orgId/admin"
              element={
                <RouteSuspense>
                  <AppErrorBoundary>
                    <OrgAdminLayout />
                  </AppErrorBoundary>
                </RouteSuspense>
              }
            >
              <Route
                index
                element={
                  <RouteSuspense>
                    <OrgAdminOverview />
                  </RouteSuspense>
                }
              />
              <Route
                path="members"
                element={
                  <RouteSuspense>
                    <OrgAdminMembers />
                  </RouteSuspense>
                }
              />
              <Route
                path="units"
                element={
                  <RouteSuspense>
                    <OrgAdminUnits />
                  </RouteSuspense>
                }
              />
              <Route
                path="invites"
                element={
                  <RouteSuspense>
                    <OrgAdminInvites />
                  </RouteSuspense>
                }
              />
              <Route
                path="settings"
                element={
                  <RouteSuspense>
                    <OrgAdminSettings />
                  </RouteSuspense>
                }
              />
              <Route
                path="integrations"
                element={
                  <RouteSuspense>
                    <OrgAdminIntegrations />
                  </RouteSuspense>
                }
              />
              <Route
                path="audit"
                element={
                  <RouteSuspense>
                    <OrgAdminAudit />
                  </RouteSuspense>
                }
              />
              <Route
                path="clients"
                element={
                  <RouteSuspense>
                    <OrgAdminClients />
                  </RouteSuspense>
                }
              />
              <Route
                path="orgchart"
                element={
                  <RouteSuspense>
                    <OrgAdminOrgChart />
                  </RouteSuspense>
                }
              />
              <Route
                path="access"
                element={
                  <RouteSuspense>
                    <OrgAdminAccess />
                  </RouteSuspense>
                }
              />
              <Route
                path="deployment"
                element={
                  <RouteSuspense>
                    <OrgAdminDeployment />
                  </RouteSuspense>
                }
              />
              <Route
                path="customer-operations"
                element={
                  <RouteSuspense>
                    <OrgAdminCustomerOperations />
                  </RouteSuspense>
                }
              />
              <Route
                path="exports"
                element={
                  <RouteSuspense>
                    <OrgAdminExports />
                  </RouteSuspense>
                }
              />
            </Route>
            <Route path="/" element={<Navigate to="/home" replace />} />
          </Route>
          {/* Catch-all 404 — must be the last route. */}
          <Route
            path="*"
            element={
              <RouteSuspense>
                <AppErrorBoundary>
                  <NotFoundPage />
                </AppErrorBoundary>
              </RouteSuspense>
            }
          />
        </Routes>
      </AppErrorBoundary>
    </LazyMotion>
  );
}

export function ErrorBoundary({ error }: { error: Error | null }) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
